import { Button } from '@heroui/react'
import { useQueryClient } from '@tanstack/react-query'
import { getAuthInstance } from '@zealth/core/firebase/runtime'
import { signOut } from 'firebase/auth'
import { type ReactNode, useEffect, useRef } from 'react'

import { InsightsAccountProvider } from './InsightsAccountContext'
import SignInScreen, { CentredPanel } from './SignInScreen'
import { useInsightsSession } from './useInsightsSession'

/**
 * Default-deny: a session is not enough, the account also needs a role. Every
 * Firebase user of the project can sign in here; only a claim lets them past.
 * The callable denies the data either way — this decides what is rendered.
 */
const InsightsAuthGuard = ({ children }: { children: ReactNode }) => {
  const { user, role, isLoading, hasError, retry } = useInsightsSession()
  const queryClient = useQueryClient()

  // The payload is cached for the session and shaped by the role (an admin's
  // copy carries team_accounts). When the role changes under a signed-in
  // account — a claim granted or revoked, seen on the next token renewal —
  // the cached copy is the wrong shape for the new role: drop it and refetch.
  // The core helper only clears on a change of account, not of claims.
  const kind = role?.kind ?? null
  const previousKind = useRef<typeof kind>(null)
  useEffect(() => {
    if (previousKind.current !== null && previousKind.current !== kind) {
      void queryClient.resetQueries()
    }
    previousKind.current = kind
  }, [kind, queryClient])

  if (isLoading) {
    return (
      <CentredPanel>
        <p className="text-sm">Loading…</p>
      </CentredPanel>
    )
  }

  if (!user) return <SignInScreen />

  if (hasError) {
    return (
      <CentredPanel>
        <h2 className="mb-2 text-base font-semibold">
          Could not check this account’s access
        </h2>
        <p className="text-muted mb-6 text-sm">
          The sign-in service did not answer. Check the connection and try
          again.
        </p>
        <div className="grid gap-2">
          <Button variant="primary" fullWidth onPress={retry}>
            Try again
          </Button>
          <Button
            variant="outline"
            fullWidth
            onPress={() => signOut(getAuthInstance())}
          >
            Sign out
          </Button>
        </div>
      </CentredPanel>
    )
  }

  if (!role) {
    return (
      <CentredPanel>
        <h2 className="mb-2 text-base font-semibold">No access</h2>
        <p className="text-muted mb-6 text-sm">
          {user.email} is signed in but has no insights role. Ask an admin to
          grant one.
        </p>
        <Button
          variant="outline"
          fullWidth
          onPress={() => signOut(getAuthInstance())}
        >
          Sign out
        </Button>
      </CentredPanel>
    )
  }

  return (
    <InsightsAccountProvider
      email={user.email}
      displayName={user.displayName}
      role={role}
    >
      {children}
    </InsightsAccountProvider>
  )
}

export default InsightsAuthGuard
